/* eslint-disable no-restricted-syntax -- full-bleed video chrome: TikTok is a
   black video canvas with a white text/icon overlay throughout this file,
   matching real TikTok UI, not a Mora surface, so
   bg-background/text-foreground don't apply. Scope is the white/black
   overlay chrome only — no hex literal remains in this file, see
   bg-platform-tiktok-badge / text-platform-tiktok-verified below. */
import {
  BookmarkSimpleIcon,
  ChatCircleIcon,
  DotsThreeIcon,
  HeartIcon,
  MusicNoteIcon,
  PlusIcon,
  SealCheckIcon,
  ShareFatIcon,
} from '@phosphor-icons/react';
import React from 'react';

import { MediaSlot } from './MediaSlot';
import { PreviewAvatar } from './PreviewAvatar';
import { DEFAULT_PREVIEW_BRAND_COLOR, type PlatformPreviewBaseProps, compactNumber } from './types';
import { useCaptionFold } from './useCaptionFold';
import { useCarousel } from './useCarousel';

import { cn } from '@/lib/utils';

// TikTok's caption commonly folds around 150 characters (roughly 2 lines)
// before the "...more" affordance in the app.
const FOLD_LENGTH = 150;

/** What TikTok keeps of a caption: 2,200 characters on a video; on a photo
 * post the description is 4,000 (the title is cut at 90). Same numbers the
 * publisher clamps to (shared/tiktok-post-params.ts). */
export const TIKTOK_PREVIEW_CAPTION_LIMITS = { video: 2200, photo: 4000 } as const;

export interface TikTokPreviewProps {
  /** Video or photo post. Falls back to whether the slot holds a video. */
  tiktokMediaKind?: 'video' | 'photo';
  /** Photo audio: `tiktok-picks` when "Add recommended music" is on, `none` when off,
   * `drafts` when the post goes to TikTok drafts (sound is picked there). */
  tiktokSound?: 'tiktok-picks' | 'none' | 'drafts';
  /** Where the cover frame is taken from, for example `0:03`. Video only. */
  tiktokCoverLabel?: string | null;
}

/**
 * TikTok video — vertical 9:16 frame with overlay actions on the right rail
 * (avatar, like, comment, save, share) and bottom-left username + caption +
 * sound credit. Replicates the For You / profile feed look.
 */
export const TikTokVideo: React.FC<
  PlatformPreviewBaseProps & TikTokPreviewProps & { soundCredit?: string }
> = ({
  brandName,
  username,
  brandColor = DEFAULT_PREVIEW_BRAND_COLOR,
  avatarUrl,
  isVerified = false,
  caption,
  imageUrl,
  imageUrls,
  mediaItems,
  carouselActiveIndex,
  onCarouselActiveIndexChange,
  videoUrl,
  videoPoster,
  focalX,
  focalY,
  zoom,
  engagement,
  isLoading = false,
  soundCredit,
  tiktokMediaKind,
  tiktokSound,
  tiktokCoverLabel,
  mediaOverlay,
  className,
}) => {
  const handle = (username || brandName).toLowerCase().replace(/\s+/g, '');
  const likes = engagement?.likes;
  const comments = engagement?.comments;
  const saves = engagement?.saves;
  const shares = engagement?.shares;
  const { visible, needsFold, expanded, setExpanded } = useCaptionFold(caption, FOLD_LENGTH);
  const hasVideo = Boolean(videoUrl);
  // TikTok photo-mode ships image/carousel posts via the PHOTO Direct Post
  // path (PUBLISHER_MEDIA_KINDS.tiktok.feed) alongside video — reuse the same
  // carousel machinery every other platform-preview replica uses so a photo
  // carousel actually has something to preview instead of rendering nothing.
  const { currentImage, currentVideoUrl, currentVideoPoster, carouselOverlay } = useCarousel(
    imageUrls,
    imageUrl,
    {
      activeIndex: carouselActiveIndex,
      onActiveIndexChange: onCarouselActiveIndexChange,
      mediaItems,
    },
  );

  const mediaKind =
    tiktokMediaKind ?? ((currentVideoUrl ?? videoUrl ?? hasVideo) ? 'video' : 'photo');
  const captionLimit = TIKTOK_PREVIEW_CAPTION_LIMITS[mediaKind];
  const overLimit = expanded && (caption?.length ?? 0) > captionLimit;
  const soundLine =
    soundCredit ??
    (tiktokSound === 'drafts'
      ? 'Pick a sound in TikTok'
      : mediaKind === 'photo'
        ? tiktokSound === 'none'
          ? null
          : 'Music chosen by TikTok'
        : `original sound · ${brandName}`);

  return (
    <article
      className={cn('relative w-full aspect-[9/16] rounded-xl overflow-hidden bg-black', className)}
      data-platform="tiktok"
    >
      <MediaSlot
        fill
        bg="zinc"
        videoFirst
        // TikTok is no longer video-only (photo-mode ships image/carousel via
        // the PHOTO Direct Post path — PUBLISHER_MEDIA_KINDS.tiktok.feed), so
        // the videoFirst fallback hint 'Add video to preview' would be stale
        // copy for an empty TikTok slot; override with media-kind-neutral copy.
        emptyHint="Add media to preview"
        imageUrl={currentImage}
        videoUrl={currentVideoUrl ?? videoUrl}
        videoPoster={currentVideoPoster ?? videoPoster}
        focalX={focalX}
        focalY={focalY}
        zoom={zoom}
        isLoading={isLoading}
        alt={caption ?? `${brandName} video`}
        overlay={mediaOverlay}
      />

      <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/60 pointer-events-none" />

      {/* carouselOverlay is rendered here, not inside MediaSlot's overlay, and
          re-anchored into the one clear band this layout has (measured
          live: "Following/For You" nav ends ~30px down, the right-rail
          action stack starts ~161px down) — useCarousel's shared top-1/2
          position is correct for a card-style preview with nothing else in
          the frame, but on TikTok's full-bleed video-app layout it lands
          squarely inside the right rail's icon stack, both visually
          overlapping the comment/save/share icons AND — since the rail
          renders after this in the DOM — losing the click to whichever
          action icon is underneath it. */}
      <div className="absolute inset-x-0 top-9 bottom-96">{carouselOverlay}</div>

      <div className="absolute top-3 left-0 right-0 flex items-center justify-center gap-6 text-caption font-medium text-white">
        <span className="opacity-60">Following</span>
        <span className="relative">
          For You
          <span className="absolute left-1/2 -translate-x-1/2 -bottom-1 h-px w-4 bg-white" />
        </span>
      </div>

      <div className="absolute right-2 bottom-20 flex flex-col items-center gap-4">
        <div className="relative">
          <PreviewAvatar
            brandName={brandName}
            brandColor={brandColor}
            avatarUrl={avatarUrl}
            className="size-11 rounded-full border-2 border-white"
            textClassName="text-caption font-semibold"
          />
          <span className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 size-5 rounded-full bg-platform-tiktok-badge flex items-center justify-center">
            <PlusIcon className="size-3 text-white" weight="bold" />
          </span>
        </div>

        <ActionButton
          icon={<HeartIcon className="size-7" weight="fill" />}
          label={likes == null ? undefined : compactNumber(likes)}
        />
        <ActionButton
          icon={<ChatCircleIcon className="size-7" weight="fill" />}
          label={comments == null ? undefined : compactNumber(comments)}
        />
        <ActionButton
          icon={<BookmarkSimpleIcon className="size-7" weight="fill" />}
          label={saves == null ? undefined : compactNumber(saves)}
        />
        <ActionButton
          icon={<ShareFatIcon className="size-7" weight="fill" />}
          label={shares == null ? undefined : compactNumber(shares)}
        />
      </div>

      <div className="absolute left-3 right-16 bottom-3 text-white space-y-1.5">
        <div className="flex items-center gap-1.5">
          <p className="text-caption font-semibold">@{handle}</p>
          {isVerified ? (
            <SealCheckIcon className="size-3.5 text-platform-tiktok-verified" weight="fill" />
          ) : null}
        </div>
        {caption ? (
          <p className="text-detail leading-relaxed whitespace-pre-wrap break-words">
            {overLimit ? visible.slice(0, captionLimit) : visible}
            {overLimit ? (
              <>
                <span className="my-1 flex items-center gap-1.5 text-micro text-white/80">
                  <span className="h-px flex-1 border-t border-dashed border-white/60" />
                  TikTok keeps {captionLimit.toLocaleString('en-US')} characters
                  <span className="h-px flex-1 border-t border-dashed border-white/60" />
                </span>
                <span className="text-white/50">{visible.slice(captionLimit)}</span>
              </>
            ) : null}
            {needsFold && !expanded ? (
              <>
                {'… '}
                <button
                  type="button"
                  onClick={() => setExpanded(true)}
                  className="text-white/70 hover:text-white hover:underline transition-colors"
                >
                  more
                </button>
              </>
            ) : null}
          </p>
        ) : null}
        {soundLine ? (
          <div className="flex items-center gap-1.5 text-detail">
            <MusicNoteIcon className="size-3 shrink-0" weight="fill" />
            <span className="truncate">{soundLine}</span>
          </div>
        ) : null}
      </div>

      {mediaKind === 'video' && tiktokCoverLabel ? (
        <span className="absolute left-3 top-9 rounded-full bg-black/55 px-2 py-0.5 text-micro font-medium text-white">
          Cover at {tiktokCoverLabel}
        </span>
      ) : null}

      <div className="absolute top-3 right-3">
        <DotsThreeIcon className="size-5 text-white/90" weight="bold" />
      </div>
    </article>
  );
};

const ActionButton: React.FC<{ icon: React.ReactNode; label?: string }> = ({ icon, label }) => (
  <div className="flex flex-col items-center gap-0.5 text-white">
    <span aria-hidden>{icon}</span>
    {label != null ? <span className="text-micro font-semibold">{label}</span> : null}
  </div>
);
